/*!
 * Copyright © 2026. Verizon Connect Ireland Limited. All rights reserved.
 */

import { A11yModule } from '@angular/cdk/a11y';
import { ListRange } from '@angular/cdk/collections';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { CdkTreeModule, FlatTreeControl } from '@angular/cdk/tree';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  NgZone,
  OnDestroy,
  OnInit,
  output,
  signal,
  viewChild
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacyProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { FlatTreeDataSource, FlatTreeVirtualScrollingDataSource } from '../../../../core/components';
import { HighlightPipe } from '../../../../shared/pipes/highlight/highlight.pipe';

export type TreeNode = {
  id: string;
  name: string;
  level: number;
  isExpandable: boolean;
  attributeId: string;
  isExpanded: boolean;
  isChecked: boolean;
  isIndeterminate: boolean;
};

export const MAX_SELECTED_VALUES = 1000;

/**
 * Flat tree component with virtual scrolling and cascading checkbox selection
 * for displaying and filtering attribute hierarchies.
 *
 * Uses CdkTree with FlatTreeDataSource coordinated with CdkVirtualScrollViewport
 * to efficiently render large datasets (only visible nodes are in the DOM).
 *
 * Selection state is managed via O(1) Set lookups with pre-computed
 * parent/descendant caches for optimal performance.
 */
@Component({
  selector: 'fm-attribute-tree-node',
  standalone: true,
  imports: [
    CdkTreeModule,
    ScrollingModule,
    MatIconModule,
    MatLegacyCheckboxModule,
    MatLegacyFormFieldModule,
    MatLegacyInputModule,
    MatLegacyButtonModule,
    MatLegacyProgressSpinnerModule,
    ReactiveFormsModule,
    CommonModule,
    MatDividerModule,
    A11yModule,
    HighlightPipe
  ],
  templateUrl: './attribute-tree-node.component.html',
  styleUrl: './attribute-tree-node.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AttributeTreeNodeComponent implements OnInit, AfterViewInit, OnDestroy {
  // --- Injected dependencies ---
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _ngZone = inject(NgZone);

  // --- Inputs / Outputs / ViewChildren ---
  readonly treeNodes = input<TreeNode[]>([]);
  readonly loading = input(false);
  readonly error = input(false);
  readonly selectionChange = output<TreeNode[]>();
  readonly refreshClicked = output<void>();
  readonly virtualScroll = viewChild(CdkVirtualScrollViewport);

  // --- Public properties ---
  readonly searchFormControl = new FormControl('');
  readonly searchDebounceValue = signal('');
  readonly nodeSize = 32;
  readonly getLevel = (node: TreeNode) => node.level;
  readonly isExpandable = (node: TreeNode) => node.isExpandable;
  readonly hasChild = (_: number, node: TreeNode) => node.isExpandable;
  readonly trackById = (_: number, node: TreeNode) => node.id;
  readonly treeControl = new FlatTreeControl<TreeNode>(this.getLevel, this.isExpandable);
  readonly expandedNodesDataSource = new FlatTreeVirtualScrollingDataSource(this.treeControl);

  // --- Private filter (pure, no side effects) ---
  private readonly _filterResult = computed(() => {
    const nodes = this.treeNodes();
    const term = this.searchDebounceValue();
    return this._applyFilter(nodes, term);
  });

  // --- Public computed ---
  readonly filteredNodes = computed(() => this._filterResult().nodes);
  readonly disableReset = computed(() => !this._hasSelection()());
  readonly disableApply = computed(() => !this._selectionChanged()());
  readonly hasAttributes = computed(() => this.treeNodes().length > 0);
  readonly hasFilterResult = computed(() => this.filteredNodes().length > 0);
  readonly isMaxSelectionReached = computed(() => this._checkedLeafCount()() >= MAX_SELECTED_VALUES);

  // --- Private state ---
  private readonly _destroy$ = new Subject<void>();
  private readonly _rangeChange$ = new Subject<ListRange>();

  // --- Public data sources (depend on computed + private state) ---
  readonly treeDataSource = new FlatTreeDataSource(this.treeControl, [], this._rangeChange$, {
    start: 0,
    end: this.nodeSize
  });

  // --- Private computed ---
  private readonly _treeModel = computed(() => {
    const nodes = this.treeNodes();
    const descendantsCache = new Map<string, TreeNode[]>();
    const parentCache = new Map<string, TreeNode | null>();
    const initialCheckedIds = new Set<string>();

    let i = 0;
    while (i < nodes.length) {
      const node = nodes[i];
      if (node.isExpandable) {
        const children: TreeNode[] = [];
        let j = i + 1;
        while (j < nodes.length && nodes[j].level > node.level) {
          parentCache.set(nodes[j].id, node);
          children.push(nodes[j]);
          if (nodes[j].isChecked) {
            initialCheckedIds.add(nodes[j].id);
          }
          j++;
        }
        descendantsCache.set(node.id, children);
        i = j;
      } else {
        if (node.level === 1 && node.isChecked) {
          initialCheckedIds.add(node.id);
        }
        i++;
      }
    }
    return {
      descendantsCache,
      parentCache,
      initialCheckedIds,
      hasSelection: signal(initialCheckedIds.size > 0),
      selectionChanged: signal(false)
    };
  });
  private readonly _descendantsCache = computed(() => this._treeModel().descendantsCache);
  private readonly _parentCache = computed(() => this._treeModel().parentCache);
  private readonly _hasSelection = computed(() => this._treeModel().hasSelection);
  private readonly _selectionChanged = computed(() => this._treeModel().selectionChanged);
  private readonly _initialCheckedIds = computed(() => this._treeModel().initialCheckedIds);
  private readonly _checkedLeafCount = computed(() => {
    const initialCheckedIds = this._initialCheckedIds();
    const size = initialCheckedIds.size;
    return signal(size);
  });

  constructor() {
    effect(() => {
      const { nodes, expandedIds } = this._filterResult();
      if (this.treeDataSource) {
        this.treeDataSource.data = nodes;

        for (const node of nodes) {
          if (node.isExpandable) {
            // Mutate isExpanded in-place: FlatTreeControl relies on reference equality.
            if (expandedIds) {
              node.isExpanded = expandedIds.has(node.id);
            }
            if (node.isExpanded) {
              this.treeControl.expand(node);
            } else {
              this.treeControl.collapse(node);
            }
          }
        }

        // Force expandedNodesDataSource to recalculate visible nodes.
        // It only listens to expansionModel.changed, so we emit a no-op change.
        this.treeControl.expansionModel.changed.next({
          source: this.treeControl.expansionModel,
          added: [],
          removed: []
        });
      }
    });
  }

  ngOnInit(): void {
    this.searchFormControl.valueChanges.pipe(debounceTime(300), takeUntil(this._destroy$)).subscribe(value => {
      this.searchDebounceValue.set(value);
    });
  }

  ngAfterViewInit(): void {
    this._ngZone.runOutsideAngular(() => {
      this.virtualScroll()
        ?.renderedRangeStream.pipe(takeUntil(this._destroy$))
        .subscribe(range => {
          this._rangeChange$.next(range);
        });
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  /** Toggles the checkbox state of a node and cascades the change to descendants or parent. */
  toggleNode(node: TreeNode): void {
    node.isChecked = !node.isChecked;
    node.isIndeterminate = false;

    if (node.isExpandable) {
      this._toggleDescendants(node);
    } else {
      this._updateParentState(node);
    }

    this._updateSelectionFlags();
    this._cdr.detectChanges();
  }
  /** Resets all selections to unchecked. */
  reset(): void {
    const nodes = this.treeNodes();
    for (const n of nodes) {
      n.isChecked = false;
      n.isIndeterminate = false;
    }
    this._updateSelectionFlags();
    this._cdr.detectChanges();
  }
  /**
   * Emit filter value
   */
  apply() {
    this._emitSelection();
  }
  /** Recalculates the virtual scroll viewport size after the container is laid out. */
  checkViewportSize(): void {
    requestAnimationFrame(() => {
      this.virtualScroll()?.checkViewportSize();
    });
  }
  /** Toggles the expanded state of a parent node and syncs treeControl. */
  toggleExpand(node: TreeNode): void {
    node.isExpanded = !node.isExpanded;
    if (node.isExpanded) {
      this.treeControl.expand(node);
    } else {
      this.treeControl.collapse(node);
    }
    this._cdr.detectChanges();
  }

  /**
   * Filters the tree nodes by search term. Shows matching leaves and their parents.
   * Pure function — returns expandedIds separately; the effect() handles the mutation.
   */
  private _applyFilter(nodes: TreeNode[], term: string): { nodes: TreeNode[]; expandedIds: Set<string> | null } {
    const normalizedTerm = term.trim().toLowerCase();
    if (!normalizedTerm) {
      return { nodes, expandedIds: null };
    }

    const result: TreeNode[] = [];
    const expandedIds = new Set<string>();
    let i = 0;

    while (i < nodes.length) {
      const node = nodes[i];

      if (!node.isExpandable) {
        i++;
        continue;
      }

      const children: TreeNode[] = [];
      let j = i + 1;
      while (j < nodes.length && nodes[j].level > node.level) {
        children.push(nodes[j]);
        j++;
      }

      const parentMatches = node.name.toLowerCase().includes(normalizedTerm);
      const matchingChildren = children.filter(c => c.name.toLowerCase().includes(normalizedTerm));

      if (matchingChildren.length > 0) {
        expandedIds.add(node.id);
        result.push(node);
        result.push(...matchingChildren);
      } else if (parentMatches) {
        result.push(node);
        result.push(...children);
      }

      i = j;
    }

    return { nodes: result, expandedIds };
  }
  /** Selects or deselects all children when a parent node is toggled. */
  private _toggleDescendants(node: TreeNode): void {
    const descendants = this._descendantsCache().get(node.id) ?? [];

    for (const d of descendants) {
      d.isChecked = node.isChecked;
      d.isIndeterminate = false;
    }
  }
  /** Recalculates the parent's checked/indeterminate state based on its children. */
  private _updateParentState(node: TreeNode): void {
    const parent = this._parentCache().get(node.id);
    if (!parent) {
      return;
    }
    const descendants = this._descendantsCache().get(parent.id) ?? [];
    let checkedCount = 0;
    for (const child of descendants) {
      if (child.isChecked) {
        checkedCount++;
      }
    }

    if (checkedCount === descendants.length) {
      parent.isChecked = true;
      parent.isIndeterminate = false;
    } else if (checkedCount === 0) {
      parent.isChecked = false;
      parent.isIndeterminate = false;
    } else {
      parent.isChecked = false;
      parent.isIndeterminate = true;
    }
  }
  /** Updates hasSelection and selectionChanged flags by comparing current checked ids against the initial snapshot. */
  private _updateSelectionFlags(): void {
    const nodes = this.treeNodes();
    const currentCheckedIds = new Set<string>();
    let anyChecked = false;

    for (const n of nodes) {
      if (n.level === 1 && n.isChecked) {
        anyChecked = true;
        currentCheckedIds.add(n.id);
      }
    }

    this._checkedLeafCount().set(currentCheckedIds.size);
    this._hasSelection().set(anyChecked);

    const initial = this._initialCheckedIds();
    const changed = currentCheckedIds.size !== initial.size || [...currentCheckedIds].some(id => !initial.has(id));
    this._selectionChanged().set(changed);
  }
  /** Emits the full tree nodes array with current selection state to the parent component. */
  private _emitSelection(): void {
    this.selectionChange.emit(this.treeNodes());
  }
}
